import type { ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2, ShieldOff } from 'lucide-react';
import { useT } from '@/i18n/I18nProvider';
import { AccessGate } from '@/features/account/AccessGate';
import { Button, EmptyState, Page } from '@/shared/ui';
import { useAuthStore } from './auth.store';
import { usePermission, type PermissionRule } from './permissions';

export function FullScreenLoader() {
  return (
    <div className="flex h-dvh items-center justify-center bg-background" aria-busy>
      <Loader2 className="size-5 animate-spin text-muted-foreground" />
    </div>
  );
}

/** Gate for authenticated routes: waits for `/auth/me`, otherwise sends the user to /login. */
export function ProtectedRoute() {
  const status = useAuthStore((s) => s.status);
  const restriction = useAuthStore((s) => s.user?.restriction);
  const location = useLocation();

  if (status === 'unknown') return <FullScreenLoader />;
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (restriction) return <AccessGate restriction={restriction} />;
  return <Outlet />;
}

/** Route-level permission gate. Shows a friendly "no access" page instead of redirecting. */
export function RequirePermission({ permission, children }: { permission: PermissionRule; children: ReactNode }) {
  const allowed = usePermission(permission);
  return allowed ? <>{children}</> : <Forbidden />;
}

export function Forbidden() {
  const t = useT();
  return (
    <Page>
      <EmptyState
        icon={<ShieldOff />}
        title={t('errors.forbiddenTitle')}
        description={t('errors.forbiddenBody')}
        action={
          <Button asChild variant="secondary">
            <Link to="/">{t('errors.goHome')}</Link>
          </Button>
        }
      />
    </Page>
  );
}
